<template>
  <div>
    <component :is="fileInputContainer ? 'Portal' : 'div'"
      :to="fileInputContainer"
      :disabled="!fileInputContainer"
    >
      <div v-if="!readonly"
        class="file-input-container"
        v-on="dragListeners"
      >
        <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
        <label :for="uniqName('file-input')"
          :class="attachButtonClass"
          :disabled="disabled"
          tabindex="0"
          title="Click to choose files from your computer, or drag and drop files here"
          @keyup.enter="$event.target.click()"
        >
          <slot name="attach-button-label">Attach files...</slot>
        </label>
        <input
          :id="uniqName('file-input')"
          type="file"
          class="sr-only"
          :multiple="multiple"
          :disabled="disabled"
          tabindex="-1"
          v-bind="$attrs"
          @change="addFiles($event.target.files)"
          ref="input"
        />
      </div>
    </component>

    <div v-show-slide="allFiles.length || (isValidDropTarget && !dropZoneIsBeingShown)"
      v-on="dragListeners"
    >
      <div :class="filesContainerClass">
        <div v-for="({ file, isBeingProcessed }, i) in allFiles"
          :key="file.name + i"
        >
          <ReflectAttrs
            v-bind="{ canDelete: !isBeingProcessed && !readonly, url: getUrl(file) }"
            v-slot="{ canDelete, url }"
          >
            <div :class="['file-row', fileRowClass]"
              :dropzone="multiple ? true : null"
              @dragstart="rowDragStart($event, file.name)"
              @dragenter="setActiveDropZone($event, true)"
              @dragleave="setActiveDropZone($event, false)"
              @dragover.prevent
              @dragend="rowDragEnd"
              @drop="rowOnDrop($event, i)"
            >
              <span
                v-if="multiple && allowReorder"
                :ref="`${file.name}-${i}`"
                class="handle"
                tabindex="0"
                @mousedown="$event.target.parentElement.draggable = true"
                @mouseup="$event.target.parentElement.draggable = false"
                @keydown.up="shift(i, i - 1, file.name)"
                @keydown.down="shift(i, i + 1, file.name)"
              >&equiv;</span>

              <slot name="file-icon" :extension="file.extension || file.name.split('.').pop()"></slot>

              <slot name="file-row" :file="file" :updatefile="updateFile">
                <a v-if="url"
                  :href="url"
                  :download="file.name"
                  :class="['open-file-link', openLinkClass]"
                  @keyup.delete="canDelete && removeFile(file)"
                >{{ file.name }}</a>
                <div v-else
                  :class="['open-file-link', openLinkClass]"
                  tabindex="0"
                  @keyup.delete="canDelete && removeFile(file)"
                >{{ file.name }}</div>
              </slot>

              <slot v-if="file.error"
                name="file-error"
                :file="file"
              >
                <span :class="errorMessageClass"
                >Error: {{ file.error }}</span>
              </slot>
              <slot v-else-if="isBeingProcessed"
                name="processing"
                :file="file"
              >
                <i :class="processingMessageClass">(processing...)</i>
              </slot>
              <slot v-else name="file-info" :file="file"></slot>

              <button v-if="canDelete"
                :title="`Remove ${file.name}`"
                :disabled="disabled"
                :class="removeButtonClass"
                @click="removeFile(file)"
              >
                <slot name="remove-button-content" :file="file">
                  <span class="sr-only">Remove {{ file.name }}</span>
                  &times;
                </slot>
              </button>
            </div>
          </ReflectAttrs>
        </div>

        <div v-show-slide="isValidDropTarget"
          @slide-open-start="dropZoneIsBeingShown = true"
          @slide-open-end="dropZoneIsBeingShown = false"
        >
          <div class="drop-zone">
            <slot name="drop-zone-message">
              Drag and drop files here, including emails and attachments from Outlook
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { without } from 'lodash-es';
import ReflectAttrs from '../helpers/ReflectAttrs';

const dropEffect = 'copy';
const modelEvent = 'input';

/**
 * Returns a boolean indicating whether the event was triggered by dragging files
 * (or emails/attachments) from an external application rather than an element from
 * the current page such as a link.
 * NOTE: Array.prototype is used in order to support IE11.
 */
const isEventFileDrag = ($event) => Array.prototype.includes.call($event.dataTransfer.types, 'Files');

export default {
  inheritAttrs: false,

  components: { ReflectAttrs },

  props: {
    attachButtonClass: String,
    errorMessageClass: {
      type: String,
      default: () => 'file-error-msg'
    },
    fileRowClass: {
      type: String,
      default: () => 'file-row-default'
    },
    filesContainerClass: {
      type: String,
      default: () => 'files-container-default'
    },
    openLinkClass: {
      type: String,
      default: () => 'open-file-link-default'
    },
    processingMessageClass: String,
    removeButtonClass: {
      type: String,
      default: () => 'remove-button-default'
    },

    disabled: {
      type: Boolean,
      default: () => false
    },

    prohibitedDropExtensions: {
      type: Array,
      default: () => []
    },

    /**
     * The ID of a DOM element outside of the component in which to
     * render the file input if desired.
     */
    fileInputContainer: String,

    multiple: {
      type: Boolean,
      default: () => true
    },

    /**
     * A function used to process each file as it is attached. Useful for
     * automatically uploading new files or transforming them in some way.
     * If an error is thrown it will be displayed with the file, and an object
     * will be emitted with "name" and "error" properties.
     */
    processNewFile: {
      type: Function,
      default: (f) => f
    },

    /**
     * When read-only, files cannot be added or removed.
     */
    readonly: {
      type: Boolean,
      default: () => false
    },

    /**
     * A function used to sort the files. Defaults to alphabetical ascending. Ignored if allowReorder is true.
     */
    sortFn: {
      type: Function,
      default: (a, b) => a.name.localeCompare(b.name)
    },

    /**
     * Allow manual reordering of the files. sortFn will be ignored.
     */
    allowReorder: {
      type: Boolean,
      default: () => false
    },

    /**
     * File attachments. Should be either Blobs or objects representing persistent files
     * with the following properties:
     *  name (String) - the name of the file
     *  url (String) - the download URL of the file
     *  extension (String) - (optional) the file extension (if not extractable from name)
     *  error (String) - (optional) display an error message with the file
     */
    value: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    dragEnterCount: 0,
    dragListeners: {},
    rowDragStartPoint: -1,
    dropZoneIsBeingShown: false, // Used to coordinate slide animations
    filesBeingProcessed: [],
    isValidDropTarget: false
  }),

  computed: {
    allFiles: ({
      filesBeingProcessed, sortFn, value, allowReorder
    }) => [
      ...value.map((f) => ({ file: f, isBeingProcessed: false })),
      ...filesBeingProcessed.map((f) => ({ file: f, isBeingProcessed: true }))
    ].sort((a, b) => (!allowReorder ? sortFn(a.file, b.file) : 0))
  },

  methods: {
    async addFiles(files) {
      if (!this.multiple && files.length + this.value.length > 1) { return; }

      [...files].map(async (file) => {
        try {
          this.filesBeingProcessed.push(file);

          if (file.size <= 0) {
            throw new Error('Folders and empty files are not supported.');
          }

          // processNewFile might not be asynchronous, so coerce it to always return a chainable
          // Promise.
          const processedFile = await (async () => this.processNewFile(file))()
            // When multiple files are added simultaneously, this ensures that the modelEvent
            // will be fired after this.value has been updated for other files processed first,
            // otherwise they'll all just overwrite each other. A timeout is used because its
            // callback is added to the lower-priority task queue, while immediately resolved
            // Promises and Vue.nextTick are added to the higher-priority microtask queue
            // which is dequeued before prop updates.
            .finally(() => new Promise((resolve) => { setTimeout(() => resolve(), 0); }));

          this.$emit(modelEvent, [...this.value, processedFile]);
        } catch (e) {
          // Emit an object with the file name and error message.
          this.$emit(modelEvent, [
            ...this.value,
            { name: file.name, error: e?.message || 'Something went wrong.' }
          ]);
          this.$emit('error', e);
        } finally {
          this.filesBeingProcessed.splice(this.filesBeingProcessed.indexOf(file), 1);
        }
      });
    },

    getUrl: (file) => (file instanceof Blob ? window.URL.createObjectURL(file) : file.url),

    onDragEnter($event) {
      $event.stopPropagation();
      $event.preventDefault();

      if (
        this.dragEnterCount++ > 0
        || this.readonly
        || this.disabled
        || !isEventFileDrag($event)
      ) return;

      const { dataTransfer: d } = $event;

      d.dropEffect = dropEffect;
      const dropAllowed = [d.dropEffect, 'all'].includes(d.effectAllowed);
      this.isValidDropTarget = dropAllowed;
    },

    onDragLeave() {
      this.dragEnterCount--;
    },

    onDragOver($event) {
      if (!isEventFileDrag($event)) return;

      // Always prevent default for file drags, even if isValidDropTarget is false.
      // Otherwise, if the drop is not handled, the browser will open certain types
      // of files in a new tab.
      $event.stopPropagation();
      $event.preventDefault();
    },

    onDrop($event) {
      $event.stopPropagation();
      $event.preventDefault();

      this.dragEnterCount = 0;

      // don't allow drop if readonly or disabled, and filter out any files with a prohibited extension
      if (this.readonly || this.disabled) return;

      const { files } = $event.dataTransfer;

      const filteredFiles = [...files].filter((f) => !this.prohibitedDropExtensions.some((e) => f.name.endsWith(e)));

      this.addFiles(filteredFiles);
    },

    removeFile(file) {
      this.$emit(modelEvent, without(this.value, file));
    },

    updateFile(oldFile, newFile) {
      const index = this.value.indexOf(oldFile);
      const newValues = [...this.value];
      newValues.splice(index, 1, newFile);
      this.$emit(modelEvent, newValues);
    },

    rowDragStart(event, name) {
      if (this.allowReorder) {
        event.dataTransfer.setData('text', name);
        event.dataTransfer.effectAllowed = 'move';
        this.rowDragStartPoint = event.pageY;
      }
    },

    rowDragEnd(event) {
      event.target.parentElement.draggable = false;
    },

    rowOnDrop(event, newIndex) {
      const name = event.dataTransfer.getData('text');
      const oldIndex = this.allFiles.findIndex((f) => f.file.name === name);

      this.shift(oldIndex, newIndex);
      this.setActiveDropZone(event, false); // clear up the residual shadow if you drop the file on itself.
    },

    setActiveDropZone(event, isActive) {
      if (!this.allowReorder) return;

      const element = event.target.parentElement;

      if (isActive) {
        const side = this.rowDragStartPoint >= event.pageY ? 'before' : 'after';
        element.classList.add(`active-drop-zone-${side}`);
      } else {
        // remove both just to be safe.
        element.classList.remove('active-drop-zone-before');
        element.classList.remove('active-drop-zone-after');
      }
    },

    shift(oldIndex, newIndex, name) {
      if (newIndex < 0 || newIndex >= this.allFiles.length) { return; }
      const me = this.allFiles[oldIndex];
      const newList = without(this.allFiles, me);
      newList.splice(newIndex, 0, me);
      this.$emit(modelEvent, newList.map((f) => f.file));

      if (name) {
        setTimeout(() => {
          const el = this.$refs[`${name}-${newIndex}`][0];
          if (el) {
            el.focus();
          }
        }, 5);
      }
    },
  },

  watch: {
    dragEnterCount() {
      setTimeout(() => {
        if (this.dragEnterCount === 0) this.isValidDropTarget = false;
      }, 250);
    },

    value() {
      // If the user attaches a file, removes it, then tries to attach it
      // again, no change event will be fired unless we first clear the value.
      if (this.$refs.input) this.$refs.input.value = null;
    }
  },

  mounted() {
    const dragListeners = {
      dragenter: this.onDragEnter,
      dragover: this.onDragOver,
      dragleave: this.onDragLeave,
      drop: this.onDrop
    };

    if (this.$listeners['binding-drag-events'] || this.$listeners.bindingDragEvents) {
      this.$emit('binding-drag-events', dragListeners);
    } else {
      this.dragListeners = dragListeners;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/common";

.files-container-default {
  background-color: #f3f4f5;
  border-radius: 5px;
  padding: 0.5em 1em;
}

.file-row {
  align-items: center;
  display: flex;
  gap: 0.5em;

  &.file-row-default {
    font-size: 0.9em;

    &:hover {
      background-color: #e6e6e6;
    }

    ::v-deep {
      a, button {
        font-size: inherit;
      }
    }
  }
}

.open-file-link {
  flex-grow: 1;
  text-align: left;
  white-space: break-spaces;
  word-break: break-word;
}
.open-file-link-default {
  padding: 0.25em 0.5em;
}

.remove-button-default {
  font-size: 0.85em;
}

.file-error-msg {
  color: red;
}

.drop-zone {
  border: 3px dashed #738ca1;
  color: #355571;
  font-style: italic;
  margin-bottom: 0.5em;
  margin-top: 1em;
  opacity: 0.7;
  padding: 1em;
  text-align: center;
}

.active-drop-zone-before {
  box-shadow: 0px 2px lightgrey inset;
}

.active-drop-zone-after {
  box-shadow: 0px -2px lightgrey inset;
}

.handle {
  cursor: move;
  margin: 5px;
}
</style>
